"use client";

import React from "react";
import { CloudUpload, Cpu, Box } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const processSteps = [{
  id: "upload",
  title: "导入听力素材",
  description: "支持多种音频、视频格式或播客链接，一键上传至云端，AI 引擎即刻开启深度解析模式。",
  icon: CloudUpload
}, {
  id: "process",
  title: "AI 智能切分",
  description: "自研 Sentence Detection 引擎以 99% 的准确率自动识别句子边界，同步生成精准的中英对照文案。",
  icon: Cpu
}, {
  id: "deliver",
  title: "沉浸式精听",
  description: "基于间隔重复算法，支持单句无限循环、倍速播放与单词即点即查，确保攻克每一个听力难点。",
  icon: Box
}];
function ProcessPillar({
  step,
  index
}: {
  step: typeof processSteps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const Icon = step.icon;
  return <div data-section-id="73467" ref={ref} data-index={index} className={`relative z-10 flex flex-col items-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 200}ms`
  }}>
      <div className="mb-8 relative">
        {}
        <div className="absolute inset-0 bg-primary/10 blur-2xl rounded-full scale-150"></div>
        
        <div className="relative flex items-center justify-center w-20 h-20 rounded-[1.25rem] bg-card border-2 border-primary/10 shadow-lg">
          <Icon className={`w-10 h-10 text-primary drop-shadow-[0_0_8px_rgba(var(--color-primary),0.4)] ${isVisible ? 'animate-draw-icon' : 'opacity-0'}`} strokeWidth={2} />
        </div>
      </div>

      <Card className="w-full bg-card border-2 border-muted shadow-xl rounded-[2rem] hover:border-primary/20 transition-all duration-500 hover:-translate-y-2">
        <CardHeader className="text-center pb-2">
          <div className="text-secondary font-bold text-sm mb-2 font-sans tracking-widest uppercase">
            STEP 0{index + 1}
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">{step.title}</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <CardDescription className="text-foreground/70 text-base leading-relaxed font-medium">
            {step.description}
          </CardDescription>
        </CardContent>
      </Card>
    </div>;
}
export default function TheTransformationProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="ai-辅助精听系统" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes draw-svg {
          0% { stroke-dasharray: 100; stroke-dashoffset: 100; opacity: 0; }
          10% { opacity: 1; }
          100% { stroke-dasharray: 100; stroke-dashoffset: 0; opacity: 1; }
        }
        .animate-draw-icon path, .animate-draw-icon circle, .animate-draw-icon rect, .animate-draw-icon polyline, .animate-draw-icon line {
          animation: draw-svg 2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        
        @keyframes flow-data {
          to { stroke-dashoffset: -24; }
        }
        .animate-flow {
          animation: flow-data 1s linear infinite;
        }

        .bg-playful-grid {
          background-image: 
            linear-gradient(to right, rgba(88,204,2,0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(88,204,2,0.05) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%);
        }
      `
    }} />

      {}
      <div className="absolute inset-0 z-0 bg-playful-grid pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-6 border-primary/20 text-primary bg-primary/10 backdrop-blur-sm px-6 py-2 text-sm font-bold tracking-widest uppercase rounded-full">
            核心技术
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight text-foreground">
            AI 驱动的精听黑科技
          </h2>
          <p className="text-xl text-foreground/60 font-medium">
            采用业内领先的语音识别与自然语言处理技术，将复杂的听力材料拆解为易于消化的知识单元，让进步清晰可见。
          </p>
        </div>

        {}
        <div className="relative max-w-6xl mx-auto">
          
          {}
          <div className="hidden lg:block absolute top-10 left-[16.66%] right-[16.66%] h-[2px] z-0 pointer-events-none">
            <svg className="w-full h-full overflow-visible" preserveAspectRatio="none">
              {}
              <line x1="0" y1="0" x2="100%" y2="0" stroke="currentColor" strokeWidth="3" strokeDasharray="8 8" className="text-muted" />
              {}
              <line x1="0" y1="0" x2="100%" y2="0" stroke="currentColor" strokeWidth="3" strokeDasharray="12 12" className="text-secondary animate-flow drop-shadow-[0_0_8px_rgba(var(--color-secondary),0.6)]" />
            </svg>
          </div>

          {}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
            {processSteps.map((step, index) => <ProcessPillar key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}